'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { StaticImageData } from 'next/image'

export interface FoodPortionSelection {
  uid: string;
  quantity: number;
}

export interface FoodAddOnOptionSelection {
  uid: string;
  quantity: number;
}

export interface FoodAddOnGroupSelection {
  uid: string;
  addOnGroupOption: FoodAddOnOptionSelection[];
}

export interface FoodBundleConfigSelection {
  uid: string;
  quantity: number;
}

export interface FoodSelection {
  type: 'Simple' | 'Customizable' | 'Bundle';
  productUid: string; // the food item's uid — used as product_id in the order
  servingType?: string;
  portion?: FoodPortionSelection[];
  addOnGroup?: FoodAddOnGroupSelection[];
  bundleConfig?: FoodBundleConfigSelection;
}

export interface CartProduct {
  id: number | string
  originalProductId?: string
  product_id?: string
  name: string
  price: number
  image: string | StaticImageData
  description: string
  maxQuantity?: number
  variant?: {
    size: string
    color: string
    price?: number
  }
  // Present only for food items
  foodSelection?: FoodSelection
}

interface CartItem extends CartProduct {
  quantity: number
}

interface CartContextType {
  cart: CartItem[]
  addToCart: (product: CartProduct, quantity?: number) => void
  removeFromCart: (productId: number | string) => void
  updateQuantity: (productId: number | string, quantity: number) => void
  clearCart: () => void
  getCartTotal: () => number
  getCartItemCount: () => number
  isFoodCart: () => boolean
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const getCartKey = () => {
  if (typeof window === 'undefined') return 'swiftree_cart';
  const match = window.location.pathname.match(/\/storefront\/([^/]+)/);
  return match ? `swiftree_cart_${match[1]}` : 'swiftree_cart';
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const key = getCartKey();
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) setCart(parsed);
      }
    } catch (e) {
      console.error('Failed to rehydrate cart:', e);
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(getCartKey(), JSON.stringify(cart));
    } catch (e) {
      console.error('Failed to persist cart:', e);
    }
  }, [cart, hydrated]);

  const addToCart = (product: CartProduct, quantity: number = 1) => {
    setCart(prevCart => {
      const productUid = product.foodSelection?.productUid;
      const currentProductQuantity = productUid
        ? prevCart
            .filter(item => item.foodSelection?.productUid === productUid)
            .reduce((total, item) => total + item.quantity, 0)
        : 0;
      const allowedQuantity = product.maxQuantity !== undefined
        ? Math.min(quantity, Math.max(0, product.maxQuantity - currentProductQuantity))
        : quantity;

      if (allowedQuantity <= 0) return prevCart;

      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        const newQty = existingItem.quantity + allowedQuantity;
        if (newQty <= 0) return prevCart.filter(item => item.id !== product.id);
        return prevCart.map(item =>
          item.id === product.id ? { ...item, quantity: newQty } : item
        );
      } else {
        return [...prevCart, { ...product, quantity: allowedQuantity }];
      }
    });
  };

  const removeFromCart = (productId: number | string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: number | string, quantity: number) => {
    if (quantity <= 0) { removeFromCart(productId); return; }
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id !== productId) return item;

        const productUid = item.foodSelection?.productUid;
        const otherProductQuantity =
          productUid && item.maxQuantity !== undefined
            ? prevCart
                .filter(
                  cartItem =>
                    cartItem.id !== productId &&
                    cartItem.foodSelection?.productUid === productUid
                )
                .reduce((total, cartItem) => total + cartItem.quantity, 0)
            : 0;
        const nextQuantity = item.maxQuantity !== undefined
          ? Math.min(quantity, Math.max(0, item.maxQuantity - otherProductQuantity))
          : quantity;

        return { ...item, quantity: nextQuantity };
      })
    );
  };

  const clearCart = () => {
    setCart([]);
    try { localStorage.removeItem(getCartKey()); } catch (e) {
      console.error('Failed to clear cart from storage:', e);
    }
  };

  const getCartTotal = () =>
    cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const getCartItemCount = () =>
    cart.reduce((count, item) => count + item.quantity, 0);

  // Returns true if ALL items in the cart are food items
  const isFoodCart = () =>
    cart.length > 0 && cart.every(item => !!item.foodSelection);

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity,
      clearCart, getCartTotal, getCartItemCount, isFoodCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) throw new Error('useCart must be used within a CartProvider')
  return context
}
